import { IconName, withMixpanelSource } from '@talos/kyoko';
import { MixpanelEventSource } from '@talos/kyoko/src/tokens';
import type { IDockviewPanelProps } from 'dockview';
import {
  MonitoringEntityPanelIconLookup,
  MonitoringIconLookup,
} from '../../../../containers/Monitoring/Blotters/constants';
import type { EntityName } from '../../../../containers/Monitoring/Blotters/types';
import {
  ENTITY_MONITORING_PANEL_TYPES,
  entityByRequestName,
  entityDisplayNames,
} from '../../../../containers/Monitoring/Blotters/types';
import type { EntityOverrideMonitoringPanelConfig, PanelComponentSpec } from '../../../../types/LayoutConfig';
import { MonitoringBlotterPanel } from './MonitoringBlotterPanel';

const ENTITY_MIXPANEL_SOURCE: Record<EntityName, MixpanelEventSource> = {
  Order: MixpanelEventSource.MonitoringOrdersBlotter,
  Trade: MixpanelEventSource.MonitoringTradesBlotter,
  Quote: MixpanelEventSource.MonitoringQuotesBlotter,
  ExecutionReport: MixpanelEventSource.MonitoringExecutionReportsBlotter,
  MarketOrder: MixpanelEventSource.MonitoringMarketOrdersBlotter,
  MarketQuote: MixpanelEventSource.MonitoringMarketQuotesBlotter,
  MarketTrade: MixpanelEventSource.MonitoringMarketTradesBlotter,
  MarketExecutionReport: MixpanelEventSource.MonitoringMarketExecutionReportsBlotter,
  CustomerOrder: MixpanelEventSource.MonitoringCustomerOrdersBlotter,
  CustomerQuote: MixpanelEventSource.MonitoringCustomerQuotesBlotter,
  CustomerExecutionReport: MixpanelEventSource.MonitoringCustomerExecutionReportsBlotter,
  CustomerTrade: MixpanelEventSource.MonitoringCustomerTradesBlotter,
  AssetTransaction: MixpanelEventSource.MonitoringMarketCashEventsBlotter,
  BuyingPower: MixpanelEventSource.BuyingPowerBlotter,
  CareOrder: MixpanelEventSource.CareOrderBlotter,
  CareExecutionReport: MixpanelEventSource.CareExecutionReportsBlotter,
  Notification: MixpanelEventSource.MonitoringNotificationsBlotter,
  Index: MixpanelEventSource.MonitoringIndexBlotter,
  IndexMark: MixpanelEventSource.MonitoringIndexMarkBlotter,
};

/** Must match `ENTITY_MONITORING_PANEL_TYPES` index order (derived from `entityByRequestName`). */
const entityNames = Object.keys(entityByRequestName) as EntityName[];

const entityOverridePanelSpecs: PanelComponentSpec[] = entityNames.map((entity, index) => {
  const type = ENTITY_MONITORING_PANEL_TYPES[index];
  const mixpanelSource = ENTITY_MIXPANEL_SOURCE[entity];
  const renderer = withMixpanelSource(
    (props: IDockviewPanelProps<EntityOverrideMonitoringPanelConfig>) => (
      <MonitoringBlotterPanel {...props} entityOverride={entity} />
    ),
    mixpanelSource
  );
  const displayGroup = entityDisplayNames[entity].group;
  return {
    group:
      displayGroup != null
        ? { type: displayGroup, label: displayGroup, icon: MonitoringIconLookup[displayGroup] }
        : undefined,
    type,
    section: 'monitoring',
    label: entityDisplayNames[entity].name,
    renderer,
    canBeAdded: true,
    icon: MonitoringEntityPanelIconLookup[entity],
  };
});

const monitoringBlotterPanelSpec: PanelComponentSpec = {
  group: undefined,
  type: 'monitoringBlotter',
  section: 'monitoring',
  label: 'Monitoring Blotter',
  renderer: withMixpanelSource(MonitoringBlotterPanel, MixpanelEventSource.MonitoringBlotterPanel),
  canBeAdded: true,
  icon: IconName.DeviceDesktop,
};

/** A list of panels available as "monitoring" content */
export const MONITORING_PANEL_COMPONENTS: PanelComponentSpec[] = [
  monitoringBlotterPanelSpec,
  ...entityOverridePanelSpecs,
];
